import { Routes, Route } from 'react-router-dom';
import Main from './pages/main';
import Login from './pages/login';
import { Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { useAxiosInterceptor } from './hooks/useAxiosInterceptor';
import Helmetify from './components/Helmetify';
import AppLiveBackground from './components/AppLiveBackground';
import { useEffect, useState } from 'react';
import AdminSettings from './pages/admin_setting';
import { useAuth } from './hooks/useAuth';
import Settings from './pages/setting';
import { useTranslation } from 'react-i18next';
import { startItemImageSyncWorker } from './util/itemImageSyncWorker';
import { installSessionKeepAlive } from './util/sessionKeepAlive';
import { Z_INDEX } from './styles/zIndex';
import PrinterPickerHost from './components/PrinterPickerHost';
import ReceiptPromptHost from './components/ReceiptPromptHost';

const App = () => {
	useAxiosInterceptor();
	const { t } = useTranslation();
	const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
	const [showInstall, setShowInstall] = useState(false);

	const { sessionRef, requestSessionKeepAlive, api, session } = useAuth();

	// Item image hash sync every 10 minutes while signed in
	useEffect(() => {
		if (!session) return;
		return startItemImageSyncWorker(() => api);
	}, [session, api]);

	// Interaction / visibility keep-alive (debounced, single-flight, multi-tab safe).
	useEffect(() => {
		return installSessionKeepAlive({
			getSession: () => sessionRef.current,
			refresh: () => requestSessionKeepAlive(),
		});
	}, [sessionRef, requestSessionKeepAlive]);

	useEffect(() => {
		const handler = (e: Event) => {
			e.preventDefault(); // Prevent automatic prompt
			setDeferredPrompt(e);
			setShowInstall(true); // Show your custom install button
		};

		window.addEventListener("beforeinstallprompt", handler);

		return () => window.removeEventListener("beforeinstallprompt", handler);
	}, []);

	const handleInstallClick = async () => {
		if (!deferredPrompt) return;
		deferredPrompt.prompt(); // Show browser install prompt
		const choiceResult = await deferredPrompt.userChoice;
		setDeferredPrompt(null);
		setShowInstall(false);
	};

	return (
		<>
			<AppLiveBackground />
			<Box
				sx={{
					position: 'relative',
					zIndex: 1,
					height: '100%',
					minHeight: '100vh',
					width: '100%',
				}}
			>
				<Routes>
					<Route path="/login" element={
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100vh',
								width: '100%',
								background: 'transparent',
							}}
						>
							<Helmetify Component={Login} title={t("common:login.signIn")} />
						</Box>
					} />
					<Route path="/*" element={<Helmetify Component={Main} title="" />} />
					<Route path="/admin_settings" element={
						<Helmetify Component={AdminSettings} title={t("common:settings.appTitle")} />
					} />
					<Route path="/settings" element={
						<Helmetify Component={Settings} title={t("common:settings.userTitle")} />
					} />
				</Routes>
				{showInstall && (
					<button onClick={handleInstallClick}>{t("common:ui.buttons.installApp")}</button>
				)}
			</Box>
			<PrinterPickerHost />
			<ReceiptPromptHost />
			{/* Outside the app stacking context; toast token is the top of the scale. */}
			<ToastContainer
				position="bottom-right"
				style={{ zIndex: Z_INDEX.toast }}
			/>
		</>
	);
};

export default App;
